import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { ITodoItem } from '@fi-app/fi-onboarding/fi-todo/fi-todo';
import { AppTextService } from '@fi-shared/app-text';
import { FiTooltipButtonIconColors, FiTooltipIconClassType } from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { FiTodoListStoreService } from '../../fi-todo-list-editor-store.service';
import { FiContentLanguageSharedService } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared.service';
import { IFiIconTooltipButtonConfig } from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button-config.d';

@Component({
    selector: 'fi-todo-item',
    templateUrl: './fi-todo-item.component.html',
    styleUrls: ['./fi-todo-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-todo-item' },
    standalone: false,
})
export class FiTodoItemComponent {
    private appTextService = inject(AppTextService);
    private fiTodoListStoreService = inject(FiTodoListStoreService);
    private fiContentLanguageSharedService = inject(FiContentLanguageSharedService);

    public todoItem = input.required<ITodoItem>();
    public navigateToEditTodoItem = output<void>();
    public confirmTodoItemRemove = output<void>();
    public copyTodoItem = output<void>();

    protected selectedLanguageId = computed(() => this.fiTodoListStoreService.selectedLanguageId());

    protected todoItemName = computed(() => {
        return this.getCurrentContentLanguageLabelModelValue() || this.getPrioritizedContentLanguageLabelValue();
    });

    private getCurrentContentLanguageLabelModelValue = computed(() => {
        return this.todoItem().Names.find((name) => name.ContentLanguageId === this.selectedLanguageId())?.Value;
    });

    private getPrioritizedContentLanguageLabelValue = computed(() => {
        return this.fiContentLanguageSharedService.getPrioritizedContentLanguageLabelValue({
            contentLanguageLabelModels: this.todoItem().Names,
            currentSelectedLanguageId: this.selectedLanguageId(),
            isLanguageNameShown: true,
        });
    });

    private editTodoButton: IFiIconTooltipButtonConfig = {
        isIconVisible: true,
        tooltipClass: 'fi-rounded-white-tooltip',
        iconClass: 'fa-pen',
        tooltipText: this.appTextService.get('common.edit'),
        iconClassType: FiTooltipIconClassType.Light,
        onIconClick: () => this.navigateToEditTodoItem.emit(),
        iconHoverColor: FiTooltipButtonIconColors.Branded,
    };

    private copyTodoButton: IFiIconTooltipButtonConfig = {
        isIconVisible: true,
        tooltipClass: 'fi-rounded-white-tooltip',
        iconClass: 'fa-clone',
        tooltipText: this.appTextService.get('copy'),
        iconClassType: FiTooltipIconClassType.Solid,
        onIconClick: () => this.copyTodoItem.emit(),
        iconHoverColor: FiTooltipButtonIconColors.Branded,
    };

    private deleteTodoButton: IFiIconTooltipButtonConfig = {
        isIconVisible: true,
        tooltipClass: 'fi-rounded-white-tooltip',
        iconClass: 'fa-trash',
        tooltipText: this.appTextService.get('common.delete'),
        iconClassType: FiTooltipIconClassType.Regular,
        onIconClick: () => this.confirmTodoItemRemove.emit(),
        iconHoverColor: FiTooltipButtonIconColors.Red,
    };

    protected todoItemActions = computed<IFiIconTooltipButtonConfig[]>(() => {
        if (this.todoItem().HasResponses) {
            return [this.copyTodoButton, this.deleteTodoButton];
        }
        return [this.editTodoButton, this.copyTodoButton, this.deleteTodoButton];
    });
}
